"use client";
import { Track } from "@/src/lib/tracking";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { useSnapshot } from "valtio";
import { rootStore } from "@store/rootStore";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { I18nUtil } from "@utils/i18n";
import {
  GetOpportunityRequest,
  GetOpportunitySummaryRequest,
  ListOpportunitiesRequest,
  Opportunity,
  OpportunityPagination,
  OpportunityStatusEnum,
  OpportunitySummary,
  OpportunityTypeEnum,
} from "@features/api/memberApi";

// Query Keys
export const opportunitiesQueryKeys = {
  all: ["opportunities"] as const,
  summary: (params?: GetOpportunitySummaryRequest) =>
    [...opportunitiesQueryKeys.all, "summary", params] as const,
  list: (params?: ListOpportunitiesRequest, isLoggedIn?: boolean) =>
    [...opportunitiesQueryKeys.all, "list", params, isLoggedIn] as const,
  detail: (id: string, params?: GetOpportunityRequest) =>
    [...opportunitiesQueryKeys.all, "detail", id, params] as const,
};

/**
 * Get opportunity summary hook options
 */
interface UseOpportunitySummaryOptions {
  /** Request parameters */
  params?: {
    /** Winning rate types to include */
    types?: OpportunityTypeEnum[];
    /** Whether to enable data enrichment */
    "enrich.enable"?: boolean;
    /** Whether to return 180-day performance chart data */
    "enrich.with_performance.enable"?: boolean;
    market?: string;
  };
  /** Whether to enable the query */
  enabled?: boolean;
  /** Stale time in milliseconds */
  staleTime?: number;
  /** Garbage collection time in milliseconds */
  gcTime?: number;
  /** Initial data for SSR hydration */
  initialData?: OpportunitySummary[];
}

/**
 * Hook to fetch opportunity summary
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useOpportunitySummary({
 *   params: {
 *     enrich: {
 *       enable: true,
 *       with_performance: { enable: true, period: 180 }
 *     },
 *     language: "en-US"
 *   }
 * });
 * ```
 */
export const useOpportunitySummary = (
  options: UseOpportunitySummaryOptions = {},
) => {
  const { memberApi: memberApiClient } = useOspApiClientProvider();
  const { params, enabled = true, initialData, staleTime, gcTime } = options;
  const { isFetching, isLoading } = useSnapshot(rootStore.login);

  return useQuery({
    queryKey: opportunitiesQueryKeys.summary(params),
    queryFn: async (): Promise<OpportunitySummary[]> => {
      if (!memberApiClient?.opportunities?.getOpportunitySummary) {
        throw new Error("Opportunities API not available");
      }

      try {
        const response =
          await memberApiClient.opportunities.getOpportunitySummary(params);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch opportunity summary: ${response.statusText}`,
          );
        }

        return response.data?.data ?? [];
      } catch (error) {
        Track.error(error, {
          tag: "useOpportunitySummary",
          msg: "Error fetching data:",
        });
        throw error;
      }
    },
    enabled: enabled && !!memberApiClient && !isFetching && !isLoading,
    initialData,
    retry: 3,
    ...(staleTime != null ? { staleTime } : {}),
    ...(gcTime != null ? { gcTime } : {}),
  });
};

/**
 * List opportunities infinite scroll hook options
 */
interface UseListOpportunitiesInfiniteOptions {
  /** Base request parameters (excluding pagination) */
  params?: {
    /** Business type */
    type?: OpportunityTypeEnum;
    /** Status filter */
    status?: OpportunityStatusEnum;
    /** Whether to enable data enrichment */
    "enrich.enable"?: boolean;
    /** Whether to return asset detailed information */
    "enrich.with_asset.enable"?: boolean;
    /** Whether to return price */
    "enrich.with_asset.with_price"?: boolean;
    "enrich.with_asset.with_ipo"?: boolean;
    "enrich.with_asset.with_theme"?: boolean;
    market?: string;
    /** Language code */
    language?: string;
    /** stock status */
    asset_status?: "APPLYING" | "PENDING" | "LISTED";
    /** Start time filter (timestamp in milliseconds) */
    start_time?: number;
    /** End time filter (timestamp in milliseconds) */
    end_time?: number;
  };
  /** Number of items per page */
  pageSize?: number;
  /** Whether to enable the query */
  enabled?: boolean;
  /** Stale time in milliseconds */
  staleTime?: number;
  /** Garbage collection time in milliseconds */
  gcTime?: number;
  /** Initial data for SSR hydration */
  initialData?: Opportunity[];
}

/**
 * Hook to fetch opportunities list with infinite scrolling
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 *   flattenedData
 * } = useListOpportunitiesInfinite({
 *   params: {
 *     type: "AI_PICK",
 *     status: "PUBLISHED",
 *     enrich: {
 *       enable: true,
 *       with_asset: { enable: true }
 *     }
 *   },
 *   pageSize: 20
 * });
 * ```
 */
export const useListOpportunitiesInfinite = (
  options: UseListOpportunitiesInfiniteOptions = {},
) => {
  const { memberApi: memberApiClient } = useOspApiClientProvider();
  const locale = I18nUtil.getCurrentLocale();
  const language = I18nUtil.getBackgroundLanguageKey(locale);
  const {
    params,
    pageSize = 20,
    enabled = true,
    staleTime = 1000 * 60 * 2, // 2 minutes
    gcTime = 1000 * 60 * 5, // 5 minutes
    initialData,
  } = options;
  const { isLoggedIn, isFetching, isLoading } = useSnapshot(rootStore.login);

  const result = useInfiniteQuery<
    OpportunityPagination,
    Error,
    Opportunity[],
    ReturnType<typeof opportunitiesQueryKeys.list>,
    string | undefined
  >({
    queryKey: opportunitiesQueryKeys.list(
      {
        ...params,
        limit: pageSize,
      },
      isLoggedIn,
    ),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: string | undefined;
    }): Promise<OpportunityPagination> => {
      if (!memberApiClient?.opportunities?.listOpportunities) {
        throw new Error("Opportunities API not available");
      }

      try {
        const response = await memberApiClient.opportunities.listOpportunities({
          ...params,
          "enrich.enable": true,
          "enrich.with_asset.enable": true,
          "enrich.with_asset.with_price": true,
          language,
          next_token: pageParam,
          limit: pageSize,
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch opportunities list: ${response.statusText}`,
          );
        }

        return response.data?.data ?? { rows: [], total_count: 0 };
      } catch (error) {
        Track.error(error, {
          tag: "useListOpportunitiesInfinite",
          msg: "Error fetching data:",
        });
        throw error;
      }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage?.next_token) {
        return lastPage.next_token;
      }
      return undefined;
    },
    initialData: initialData
      ? {
          pages: [
            {
              rows: initialData,
              total_count: initialData.length,
            } as OpportunityPagination,
          ],
          pageParams: [undefined],
        }
      : undefined,
    initialDataUpdatedAt: 0,
    select: (data) => {
      return (
        data?.pages
          ?.map((page) => page?.rows)
          .flat()
          ?.filter((item) => !!item) ?? []
      );
    },
    enabled: enabled && !!memberApiClient && !isLoading && !isFetching,
    staleTime,
    gcTime,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    ...result,
    flattenedData: result.data ?? [],
  };
};

// Re-export types for convenience
export type {
  GetOpportunitySummaryRequest,
  ListOpportunitiesRequest,
  GetOpportunityRequest,
  OpportunitySummary,
  Opportunity,
  OpportunityPagination,
};
